import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { usePageEvent, track, captureAttribution, getAttribution } from "../lib/tracker";
import { Sparkles, ArrowRight, CheckCircle2, Building2, GraduationCap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

const HERO_IMAGE = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=75";

export default function Signup() {
  usePageEvent("signup_start");
  const { signup, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRef = (params.get("ref") || "").toUpperCase().slice(0, 12);
  const inviteToken = params.get("invite") || "";
  const [invite, setInvite] = useState(null);        // { org_name, account_type, email, message, expires_at }
  const [inviteError, setInviteError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", referral_code: initialRef });
  const [loading, setLoading] = useState(false);

  // Load invite details if ?invite=<token> is present.
  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/org/invite/${inviteToken}`);
        setInvite(data);
        setForm(f => ({ ...f, email: data.email || f.email }));
      } catch (e) {
        setInviteError(e?.response?.data?.detail || "Invitation is invalid or expired.");
      }
    })();
  }, [inviteToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const digits = (form.mobile || "").replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (invite && (form.email || "").toLowerCase() !== (invite.email || "").toLowerCase()) {
      toast.error(`This invitation was sent to ${invite.email}. Please use that email.`);
      return;
    }
    setLoading(true);
    try {
      captureAttribution();
      const attribution = getAttribution();
      await signup(form.name, form.email, form.password, form.referral_code || undefined, form.mobile, attribution);
      track("signup_complete", { referred: Boolean(form.referral_code), invited: Boolean(inviteToken) });
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "sign_up", {
            method: "email",
            referred: Boolean(form.referral_code),
          });
        }
      } catch (_) { /* analytics must never break signup */ }

      // If we came in via an invite, accept it now (consumes the license,
      // links user to the org). Non-fatal if it fails — user can still log in.
      if (inviteToken) {
        try {
          await api.post(`/org/invite/${inviteToken}/accept`);
          await refresh?.();
          track("org_invite_accepted", { account_type: invite?.account_type });
          toast.success(`You've joined ${invite?.org_name || "the organization"}.`);
        } catch (acceptErr) {
          apiErrorToast(acceptErr, "Account created, but joining the organization failed.");
        }
      } else {
        toast.success("Account created. Let's get you Offer Ready.");
      }
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      apiErrorToast(err, "Signup failed");
    } finally { setLoading(false); }
  };

  const InviteIcon = invite?.account_type === "institution" ? GraduationCap : Building2;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-aurora text-zinc-900">
      {/* LEFT VISUAL */}
      <div className="hidden lg:flex flex-col relative overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Team collaborating on career growth"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="signup-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-700/85 via-sky-600/75 to-emerald-500/75 mix-blend-multiply" />
        <div className="relative z-10 flex flex-col p-12 h-full text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-white/15 backdrop-blur rounded-md flex items-center justify-center ring-1 ring-white/30"><Sparkles size={16} /></div>
            <span className="font-display font-black text-lg">SkillGap <span className="opacity-80">Pilot</span></span>
          </Link>
          <div className="mt-auto">
            <div className="tag" style={{ background: 'rgba(255,255,255,.14)', color: '#fff', borderColor: 'rgba(255,255,255,.22)' }}>
              {invite ? "You've been invited" : "Free forever plan"}
            </div>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-6 leading-[1.02]">
              <span className="block">Make a stronger</span>
              <span className="block">first impression</span>
              <span className="block">with recruiters.</span>
            </h1>
            <ul className="text-white/90 mt-6 space-y-2 text-sm">
              {["1 free resume analysis","1 free L1 mock interview","Recruiter readiness score","Weekly streaks & badges"].map(t => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-300" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 bg-gradient-to-br from-indigo-500 to-sky-500 text-white rounded-md flex items-center justify-center"><Sparkles size={16} /></div>
            <span className="font-display font-black text-lg">SkillGap Pilot</span>
          </div>

          {inviteToken && inviteError && (
            <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 flex items-start gap-2 text-sm text-rose-800"
                 data-testid="signup-invite-error">
              <AlertTriangle size={14} className="mt-0.5"/>
              <div>{inviteError}</div>
            </div>
          )}
          {invite && (
            <div className="mb-5 rounded-md border border-sky-200 bg-sky-50 px-3 py-3" data-testid="signup-invite-banner">
              <div className="flex items-start gap-2">
                <InviteIcon size={16} className="text-sky-700 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-sky-900">
                    You've been invited to join <span data-testid="signup-invite-org">{invite.org_name}</span>
                  </div>
                  <div className="text-sky-800 text-xs mt-0.5">
                    {invite.account_type === "institution" ? "Institution" : "Corporate"} account ·{" "}
                    Signing up with <strong>{invite.email}</strong> will activate your seat.
                  </div>
                  {invite.message && (
                    <div className="mt-2 text-xs italic text-sky-900 bg-white/60 border border-sky-100 rounded px-2 py-1">
                      "{invite.message}"
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="tag">Get started</div>
          <h2 className="font-display font-black text-3xl tracking-tight mt-3">Create your <span className="rainbow-text">account</span></h2>
          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Full name</label>
              <input data-testid="signup-name" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
                className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</label>
              <input data-testid="signup-email" required type="email"
                readOnly={!!invite}
                value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                className={`mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${invite ? "bg-zinc-50 text-zinc-600" : ""}`} />
              {invite && <p className="text-xs text-zinc-500 mt-1">Locked to the invited email.</p>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Mobile number</label>
              <input data-testid="signup-mobile" required inputMode="tel" placeholder="+91 98765 43210"
                value={form.mobile}
                onChange={(e)=>setForm({...form,mobile:e.target.value})}
                className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              <p className="text-xs text-zinc-500 mt-1">One mobile = one account. Required for account recovery.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Password</label>
              <input data-testid="signup-password" required type="password" minLength={6} value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})}
                className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              <p className="text-xs text-zinc-500 mt-1">Minimum 6 characters.</p>
            </div>
            {!invite && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Referral code <span className="text-zinc-400 normal-case tracking-normal">(optional)</span></label>
                <input data-testid="signup-referral" value={form.referral_code} onChange={(e)=>setForm({...form,referral_code:e.target.value.toUpperCase().slice(0,12)})}
                  placeholder="FRIEND8X"
                  className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md uppercase tracking-wider focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
              </div>
            )}
            <button data-testid="signup-submit" disabled={loading} className="w-full h-11 rounded-md btn-gradient font-semibold hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Creating…" : <>{invite ? "Join & create account" : "Create account"} <ArrowRight size={14} /></>}
            </button>
            <p className="text-sm text-zinc-600 text-center">
              Have an account?{" "}
              <Link
                to={inviteToken ? `/login?invite=${encodeURIComponent(inviteToken)}` : "/login"}
                className="font-semibold text-indigo-600" data-testid="signup-go-login">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
